import { useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { getBarcode } from '../services/barcode.service';
import { type HttpBarcodeResponse } from '../types/barcode.type';

const BARCODE_STALE_TIME_MS = 10 * 60 * 1000;

export type UseBarcodeReturn = {
  /** Resolves a barcode on demand. */
  resolveBarcode: (value: string) => Promise<HttpBarcodeResponse | null>;
};

/**
 * Barcode lookup for scan-to-print workflows.
 */
export const useBarcode = (): UseBarcodeReturn => {
  const queryClient = useQueryClient();

  const resolveBarcode = useCallback(
    async (value: string): Promise<HttpBarcodeResponse | null> => {
      const trimmedValue = value.trim();
      if (!trimmedValue) return null;

      return queryClient.fetchQuery({
        cacheTime: BARCODE_STALE_TIME_MS,
        queryFn: () => getBarcode(trimmedValue),
        queryKey: ['devices', 'barcodes', trimmedValue],
        retry: false,
        staleTime: BARCODE_STALE_TIME_MS,
      });
    },
    [queryClient],
  );

  return { resolveBarcode };
};
